import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { YesNoComponent } from '@myriadgenetics/mgh-types';
import Button from '../../../elements/button';
import QuestionText from '../../../elements/question-text';
import { useLocation } from '../../../helpers/routing';

import { useButtonsWrapperContext } from '../../buttons-wrapper';
import { BackButton } from '../../buttons/back';
import { Popover } from '../../../elements/popover';

import './index.scss';

interface Props {
  compDef: any | YesNoComponent;
  responses: Record<string, any>;
  onResponseUpdated: (answerKey: string, val: any) => void;
  showNavigationButtons?: boolean;
}

export interface YesNoLocationState {
  state: {
    [answerKey: string]: boolean;
  };
}

function YesNo({ compDef, responses, onResponseUpdated, showNavigationButtons }: Props) {
  const { buttonsRef } = useButtonsWrapperContext();
  const { t } = useTranslation();
  const yn: YesNoComponent = compDef as YesNoComponent;
  const location = useLocation() as YesNoLocationState;
  const {
    answerKey,
    questionKey,
    noKey,
    yesKey,
    questionKeyParams, // not sure how to use this,
    tipTitle,
    tipDescription,
  } = yn.data;

  const answerChanged = useCallback(
    (val: boolean) => {
      onResponseUpdated(answerKey, val);
    },
    [onResponseUpdated, answerKey],
  );

  const response: boolean | undefined = get(responses, answerKey);

  const isOptionSelected = (answer: boolean) => (response ?? location.state[answerKey]) === answer;
  const shouldShowContinue = (response ?? location.state[answerKey]) !== undefined;

  return (
    <div className="yes-no">
      {questionKey && (
        <div className="yes-no__question-text">
          <QuestionText htmlFor="yesNoQuestion" questionKey={questionKey} questionKeyParams={questionKeyParams} />
          {tipTitle && tipDescription && <Popover title={t(tipTitle)} descriptionOne={t(tipDescription)}></Popover>}
        </div>
      )}
      <div className="yes-no__btns">
        <Button className="yes-no__btn yes-no_btn--yes text-medium--bold" onClick={() => answerChanged(true)} isSelected={isOptionSelected(true)}>
          {t(yesKey || 'yes')}
        </Button>
        <Button className="yes-no__btn yes-no_btn--no text-medium--bold" onClick={() => answerChanged(false)} isSelected={isOptionSelected(false)}>
          {t(noKey || 'no')}
        </Button>
      </div>
      {showNavigationButtons &&
        buttonsRef?.current &&
        createPortal(
          <>
            <BackButton />
            {shouldShowContinue && (
              <Button
                className="yes-no__continue-button"
                dataTestId="continue-button"
                onClick={() => answerChanged(response ?? location.state[answerKey])}
                size="small"
              >
                {t('continue')}
              </Button>
            )}
            <BackButton />
          </>,
          buttonsRef.current,
        )}
    </div>
  );
}

export default YesNo;
